// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { createFileRoute, useBlocker, useNavigate } from "@tanstack/react-router";
import { Camera } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useStoreDetailQuery } from "@/api/hooks/use-stores";
import { PageHeader } from "@/components/layout/page-header";
import { IdPill } from "@/components/shared/id-pill";
import { KeyChip } from "@/components/shared/key-chip";
import { DetailPageSkeleton, PageProgressBar } from "@/components/shared/page-loading";
import { RequirePermission } from "@/components/shared/require-permission";
import { StatusIndicator } from "@/components/shared/status-indicator";
import { UnsavedChangesDialog } from "@/components/shared/unsaved-changes-dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsIndicator, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CutOffTimesTab } from "@/features/cut-off-times/cut-off-times-tab";
import { CompareStoreDialog } from "@/features/stores/compare-stores-dialog";
import { CreateStoreDialog } from "@/features/stores/create-store-dialog";
import { StoreActionsMenu } from "@/features/stores/store-actions-menu";
import { StoreActivityTab } from "@/features/stores/store-activity-tab";
import { StoreDevicesTab } from "@/features/stores/store-devices-tab";
import { StoreEditTab, type StoreEditTabHandle } from "@/features/stores/store-edit-tab";
import { StoreHardwareTab, type StoreHardwareTabHandle } from "@/features/stores/store-hardware-tab";
import { StoreHealthTab } from "@/features/stores/store-health-tab";
import { StoreImportsTab } from "@/features/stores/store-imports-tab";
import { StoreInfoTab } from "@/features/stores/store-info-tab";
import { StoreMapTab } from "@/features/stores/store-map-tab";
import { StoreOperationsTab, type StoreOperationsTabHandle } from "@/features/stores/store-operations-tab";
import { StoreScheduleTab, type StoreScheduleTabHandle } from "@/features/stores/store-schedule-tab";
import { StoreSettingsTab } from "@/features/stores/store-settings-tab";
import { ToggleStoreActiveDialog } from "@/features/stores/toggle-store-active-dialog";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";
import { useDebugMode } from "@/lib/debug-mode";

export const Route = createFileRoute("/_authenticated/stores/$storeName")({
  validateSearch: z.object({ t: z.string().optional(), f: z.string().optional() }),
  component: () => (
    <RequirePermission permission={PAGE_PERMISSIONS.stores}>
      <StoreDetailPage />
    </RequirePermission>
  ),
});

function StoreDetailPage() {
  const { storeName } = Route.useParams();
  const { t: tab, f } = Route.useSearch();
  const { hasPermission } = useAuth();
  const { debugMode } = useDebugMode();
  const canEdit = hasPermission(PAGE_PERMISSIONS.storesEdit);
  const canViewHealth = hasPermission(PAGE_PERMISSIONS.storesHealth);
  const canViewOperations = hasPermission(PAGE_PERMISSIONS.storesOperations);
  const canViewHardware = hasPermission(PAGE_PERMISSIONS.storesHardware);
  const canViewSchedule = hasPermission(PAGE_PERMISSIONS.storesSchedule);
  const canViewCutOffTimes = hasPermission(PAGE_PERMISSIONS.storesCutOffTimes);
  const canViewDevices = hasPermission(PAGE_PERMISSIONS.storesDevices);
  const canViewMap = hasPermission(PAGE_PERMISSIONS.storesMap);
  const canViewImports = hasPermission(PAGE_PERMISSIONS.ingressImports);
  const canViewSettings = hasPermission(PAGE_PERMISSIONS.storesSettings);
  // Activity log is work-in-progress — debug-mode only (in addition to the view permission).
  const canViewActivity = hasPermission(PAGE_PERMISSIONS.storesActivity) && debugMode;
  const tabAllowed: Record<string, boolean> = {
    info: true,
    health: canViewHealth,
    edit: canEdit,
    operations: canViewOperations,
    hardware: canViewHardware,
    schedule: canViewSchedule,
    "cut-off-times": canViewCutOffTimes,
    devices: canViewDevices,
    map: canViewMap,
    imports: canViewImports,
    settings: canViewSettings,
    activity: canViewActivity,
  };
  const requestedTab = tab ?? "info";
  const activeTab = tabAllowed[requestedTab] ? requestedTab : "info";
  const navigate = useNavigate();
  const { t } = useTranslation("back-office");
  const { data: storeRaw, isLoading, isError } = useStoreDetailQuery(storeName);
  const [isEditDirty, setIsEditDirty] = useState(false);
  const [isOperationsDirty, setIsOperationsDirty] = useState(false);
  const [isHardwareDirty, setIsHardwareDirty] = useState(false);
  const [isScheduleDirty, setIsScheduleDirty] = useState(false);
  const isFormDirty = isEditDirty || isOperationsDirty || isHardwareDirty || isScheduleDirty;
  const editTabRef = useRef<StoreEditTabHandle>(null);
  const operationsTabRef = useRef<StoreOperationsTabHandle>(null);
  const hardwareTabRef = useRef<StoreHardwareTabHandle>(null);
  const scheduleTabRef = useRef<StoreScheduleTabHandle>(null);

  const blocker = useBlocker({
    shouldBlockFn: () => isFormDirty,
    enableBeforeUnload: () => isFormDirty,
    withResolver: true,
  });

  function handleTabChange(tab: string) {
    navigate({
      to: "/stores/$storeName",
      params: { storeName },
      search: tab === "info" ? {} : { t: tab },
      replace: true,
    });
  }

  const store = storeRaw as Record<string, unknown> | undefined;
  const displayName = (store?.legibleName as string) ?? storeName;
  const chainName = store?.chainName as string | undefined;
  const timeZone = store?.details as { timeZone?: string } | undefined;
  const isActive = store?.isActive as boolean | undefined;
  const cameraCount = (store?.cameras as { activeCount?: number } | undefined)?.activeCount;
  const breadcrumbs = useMemo(() => [{ label: t("stores.title"), to: "/stores" }], [t]);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [compareDialogOpen, setCompareDialogOpen] = useState(false);
  const [toggleStoreOpen, setToggleStoreOpen] = useState(false);

  if (isLoading) {
    return (
      <div>
        <PageProgressBar />
        <DetailPageSkeleton />
      </div>
    );
  }

  if (isError) {
    return (
      <div>
        <PageHeader title={storeName} breadcrumbs={breadcrumbs} />
        <div className="p-6">
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
            {t("errors.loadFailed")}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={displayName}
        subtitle={
          <>
            {store?.storeId !== undefined && <IdPill id={store.storeId as number} />}
            {chainName && <KeyChip label={chainName} variant="chain" />}
            <KeyChip label={storeName} variant="store" />
            {store?.isActive !== undefined && <StatusIndicator active={store.isActive as boolean} />}
            {cameraCount !== undefined && (
              <Badge variant="secondary" className="gap-1.5 font-normal text-xs [&>svg]:size-2.5">
                <Camera />
                {t("stores.cameraCount", { count: cameraCount })}
              </Badge>
            )}
            <StoreActionsMenu
              isActive={isActive ?? false}
              onDuplicate={() => setCreateDialogOpen(true)}
              onCompareSettings={() => setCompareDialogOpen(true)}
              onToggleActive={() => setToggleStoreOpen(true)}
              triggerClassName="ml-auto -my-1.5"
            />
          </>
        }
        breadcrumbs={breadcrumbs}
      />
      <div>
        <Tabs value={activeTab} onValueChange={handleTabChange} className="gap-0">
          <div className="w-full overflow-x-auto overflow-y-hidden pl-5 pr-6 pt-6">
            <TabsList>
              <TabsIndicator />
              <TabsTrigger value="info">{t("tabs.info")}</TabsTrigger>
              {canViewHealth && <TabsTrigger value="health">{t("tabs.health")}</TabsTrigger>}
              {canEdit && <TabsTrigger value="edit">{t("tabs.edit")}</TabsTrigger>}
              {canViewOperations && <TabsTrigger value="operations">{t("tabs.operations")}</TabsTrigger>}
              {canViewHardware && <TabsTrigger value="hardware">{t("tabs.hardware")}</TabsTrigger>}
              {canViewSchedule && <TabsTrigger value="schedule">{t("tabs.schedule")}</TabsTrigger>}
              {canViewCutOffTimes && <TabsTrigger value="cut-off-times">{t("tabs.cutOffTimes")}</TabsTrigger>}
              {canViewDevices && <TabsTrigger value="devices">{t("tabs.devices")}</TabsTrigger>}
              {canViewMap && <TabsTrigger value="map">{t("tabs.map")}</TabsTrigger>}
              {canViewImports && <TabsTrigger value="imports">{t("tabs.imports")}</TabsTrigger>}
              {canViewSettings && <TabsTrigger value="settings">{t("tabs.settings")}</TabsTrigger>}
              {canViewActivity && <TabsTrigger value="activity">{t("tabs.activityLog")}</TabsTrigger>}
            </TabsList>
          </div>
          <TabsContent value="info" className="space-y-4">
            <StoreInfoTab storeName={storeName} />
          </TabsContent>
          {canViewHealth && (
            <TabsContent value="health" className="space-y-4">
              <StoreHealthTab storeName={storeName} />
            </TabsContent>
          )}
          {canEdit && (
            <TabsContent value="edit" className="space-y-4">
              <StoreEditTab ref={editTabRef} storeName={storeName} onDirtyChange={setIsEditDirty} />
            </TabsContent>
          )}
          {canViewOperations && (
            <TabsContent value="operations" className="space-y-4">
              <StoreOperationsTab ref={operationsTabRef} storeName={storeName} onDirtyChange={setIsOperationsDirty} />
            </TabsContent>
          )}
          {canViewSchedule && (
            <TabsContent value="schedule" className="space-y-4">
              <StoreScheduleTab ref={scheduleTabRef} storeName={storeName} onDirtyChange={setIsScheduleDirty} />
            </TabsContent>
          )}
          {canViewCutOffTimes && (
            <TabsContent value="cut-off-times" className="space-y-4">
              <CutOffTimesTab
                target={{ kind: "store", storeName }}
                initialFilters={activeTab === "cut-off-times" ? f : undefined}
              />
            </TabsContent>
          )}
          {canViewDevices && (
            <TabsContent value="devices" className="space-y-4">
              <StoreDevicesTab storeName={storeName} />
            </TabsContent>
          )}
          {canViewHardware && (
            <TabsContent value="hardware" className="space-y-4">
              <StoreHardwareTab ref={hardwareTabRef} storeName={storeName} onDirtyChange={setIsHardwareDirty} />
            </TabsContent>
          )}
          {canViewSettings && (
            <TabsContent value="settings" className="space-y-4">
              <StoreSettingsTab storeName={storeName} initialFilters={activeTab === "settings" ? f : undefined} />
            </TabsContent>
          )}
          {canViewMap && (
            <TabsContent value="map" className="space-y-4">
              <StoreMapTab storeName={storeName} />
            </TabsContent>
          )}
          {canViewImports && (
            <TabsContent value="imports" className="space-y-4">
              <StoreImportsTab storeName={storeName} initialFilters={activeTab === "imports" ? f : undefined} />
            </TabsContent>
          )}
          {canViewActivity && (
            <TabsContent value="activity" className="space-y-4">
              <StoreActivityTab storeName={storeName} initialFilters={activeTab === "activity" ? f : undefined} />
            </TabsContent>
          )}
        </Tabs>
      </div>

      <CreateStoreDialog
        key={`duplicate-${storeName}`}
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        duplicateFrom={{
          copyFrom: storeName,
          chainName: chainName ?? "",
          timeZone: timeZone?.timeZone ?? "",
        }}
      />

      <CompareStoreDialog defaultStoreName={storeName} open={compareDialogOpen} onOpenChange={setCompareDialogOpen} />

      <ToggleStoreActiveDialog
        store={
          toggleStoreOpen && store
            ? {
                storeName,
                legibleName: displayName,
                isActive: isActive ?? false,
              }
            : null
        }
        onClose={() => setToggleStoreOpen(false)}
      />

      <UnsavedChangesDialog
        open={blocker.status === "blocked"}
        onStay={() => blocker.reset?.()}
        onDiscard={() => {
          editTabRef.current?.reset();
          operationsTabRef.current?.reset();
          hardwareTabRef.current?.reset();
          scheduleTabRef.current?.reset();
          blocker.proceed?.();
        }}
      />
    </div>
  );
}
